/**
 * dialogs.js
 */

import React from 'react';
import axios from 'axios';
import {
  useMediaQuery,
  Button as MuiButton,
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { formatISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import { getDatesFromRangeType } from 'utils/datetime';
import { getFilterParamsObj, getFilterParamsString } from 'utils/filter-params';
import logger from 'utils/logger';
import { makeLocation } from 'utils/routing';
import Button from 'components/button';
import Icon from 'components/icon';
import { DialogTitle, DialogActions, DialogActionsEnhanced, DialogTitleDropMenu } from 'components/dialog/dialog';
import ConfirmDialog from 'components/confirm-dialog';
import FilterForm from './filter-form';
import { RecordsContext } from '../records-context';


export default function FilterDialog(props) {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar(); 
  const { 
    searchOptions,
    setSearchOptions, 
    isFilterOpen, 
    setIsFilterOpen, 
    isSearchSaveOpen, 
    setIsSearchSaveOpen 
  } = React.useContext(RecordsContext);

  const history = useHistory();
  const basePath = useRouteMatch();
  const params = basePath.params;
  const filterParamsString = params.filterParamsString;
  const filterParams = getFilterParamsObj(filterParamsString);
  const filterMode = params.filterMode || '';
  const activeSavedSearchId = filterMode === Types.FilterMode.SAVED_SEARCH
    ? +params.filterOption
    : null;

  const [customSearchName, setCustomSearchName] = React.useState('');
  const [confirmDeleteSavedSearch, setConfirmDeleteSavedSearch] = React.useState();
  const [deletingSavedSearch, setDeletingSavedSearch] = React.useState();
  const [isFilterFormValid, setIsFilterFormValid] = React.useState(true);

  const isLoading = !searchOptions;
  

  // build saved search options
  const generateSavedSearchMenuOptions = () => {
    return searchOptions?.savedSearches?.map(s => ({
      ...s,
      icon: 'bookmark',
      active: s.id === activeSavedSearchId,
      onSelect: () => onSelectSavedSearch(s),
      secondaryActionIcon: <Icon type='delete' />,
      onSecondaryAction: () => onDeleteSavedSearch(s.id),
    })) || [];
  }


  // select saved search
  const onSelectSavedSearch = (option) => {
    const { fields } = option;

    // Update start/end dates based on dateRange type
    if (fields.dateRange) {
      const { startDate, endDate } = getDatesFromRangeType(fields.dateRange);
      fields.startDate = formatISO(startDate);
      fields.endDate = formatISO(endDate);
    }

    setIsFilterOpen(false);
    const paramString = getFilterParamsString(fields);
    history.push(makeLocation('', 'records', 'params', {
      p1: Types.FilterMode.SAVED_SEARCH,
      p2: option.id,
      filterParams: paramString
    }));
  }


  // save custom search
  const onSearchSave = () => {
    setIsSearchSaveOpen(false);
    logger.log('[records.js] Saving search.', filterParams);

    const savedSearch = {
      id: null,
      name: customSearchName,
      fields: JSON.stringify(filterParams),
    };
    setCustomSearchName('');

    (async () => {
      const response = await axios.post('records/save-search', savedSearch);

      if (response.status === 200) {
        const savedSearchData = response.data;
        savedSearchData.fields = JSON.parse(savedSearchData.fields);

        setSearchOptions((prev) => ({
          ...prev,
          savedSearches: [
            ...prev.savedSearches,
            savedSearchData,
          ],
        }));

        history.push(makeLocation('', 'records', 'params', {
          p1: Types.FilterMode.SAVED_SEARCH,
          p2: savedSearchData.id,
          filterParams: filterParamsString
        }));
      }
    })();
  }


  // delete saved search
  const onDeleteSavedSearch = async (searchId) => {
    setDeletingSavedSearch(searchOptions.savedSearches.find(s => s.id === searchId));
    const confirmed = await confirmDeleteSavedSearch();

    if (confirmed) {
      const response = await axios.delete(`records/saved-search/${searchId}`);

      if (response.status === 200) {
        // remove the saved search from the state
        setSearchOptions((prev) => ({
          ...prev,
          savedSearches: prev.savedSearches.filter(s => s.id !== searchId)
        }));

        // reset if this search is currently active
        if (activeSavedSearchId === searchId) {
          history.push('/records');
        }

        enqueueSnackbar('Search deleted', {
          variant: 'success'
        });
      }
    }
    setDeletingSavedSearch();
  }


  return (
    <>
      {/* FILTER DIALOG */}
      <MuiDialog
        open={!isLoading && isFilterOpen}
        sx={{ '& .MuiDialog-paper': { maxWidth: 800 } }}
        fullWidth={true}
        fullScreen={isMobile}
        onClose={() => setIsFilterOpen(false)}
      >
        <DialogTitle
          id="filter-dialog-title"
          icon="custom"
          title="Filter"
          actions={
            <DialogTitleDropMenu
              label={(isOpen, onClick, arrowIcon) => (
                <MuiButton
                  startIcon={<Icon type='bookmark' />}
                  endIcon={arrowIcon}
                  children={'Saved Searches'}
                  onClick={onClick}
                />
              )}
              items={generateSavedSearchMenuOptions()}
            />
          }
        />
        <MuiDialogContent sx={{ '&.MuiDialogContent-root': { py : 4 }}}>
          <FilterForm
            options={searchOptions}
            setIsFilterFormValid={setIsFilterFormValid}
            onSubmit={() => setIsFilterOpen(false)}
          />
        </MuiDialogContent>

        <DialogActionsEnhanced>
          <MuiButton
            children='Cancel'
            onClick={() => setIsFilterOpen(false)}
          />
          <Button
            form="filter-form"
            type="submit"
            variant="contained"
            color="secondary"
            startIcon={<Icon type="checkmark" size="small" />}
            disabled={!isFilterFormValid}
          >
            APPLY
          </Button>
        </DialogActionsEnhanced>
      </MuiDialog>


      {/* SAVE SEARCH DIALOG */}
      <MuiDialog
        open={isSearchSaveOpen}
        onClose={() => setIsSearchSaveOpen(false)}
        sx={{ '& .MuiDialog-paper': { maxWidth: 360 } }}
        fullWidth
      >
        <DialogTitle id="save-search-dialog-title"
          icon="bookmark"
          title="Save New Search"
        />
        <MuiDialogContent>
          <Typography sx={{pb: 2}}>
            Do you use this set of filters often? Save this search for next time by typing a name below. Quickly access your searches using the dropdown in the Filter dialog.
          </Typography>
          <TextField
            autoFocus
            autoComplete='off'
            margin='dense'
            size='small'
            id="name"
            variant="outlined"
            fullWidth
            label="Name"
            value={customSearchName}
            onChange={e => setCustomSearchName(e.target.value)}
          />
        </MuiDialogContent>
        <DialogActions>
          <Button onClick={() => setIsSearchSaveOpen(false)}>
            Cancel
          </Button>
          <Button onClick={onSearchSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </MuiDialog>


      {/* DELETE SAVE SEARCH CONFIRMATION DIALOG */}
      <ConfirmDialog
        title="Delete Saved Search"
        titleStyle="error"
        titleIcon="warning"
        content={`Permanently delete the search "${deletingSavedSearch?.name}"?`}
        setConfirm={setConfirmDeleteSavedSearch}
        customActions={[
          {
            label: 'Cancel',
            value: false,
            isNotPriority: true,
          },
          {
            label: 'Delete',
            value: true,
          }
        ]}
      />
    </>
  );
}